import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  TextField,
  Alert,
} from "@mui/material";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FriendlyButton } from "../shared";

const formatDateOnlyUtc = (date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
    date.getUTCDate()
  ).padStart(2, "0")}`;

const parseDateOnlyUtc = (value) => {
  if (!value) return null;
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
  );
};

const todayUtcDateOnly = () => {
  const today = new Date();
  return new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
};

const toDateInput = (value) => {
  const today = todayUtcDateOnly();
  const parsed = parseDateOnlyUtc(value);
  return formatDateOnlyUtc(!parsed || parsed < today ? today : parsed);
};

const daysInUtcMonth = (year, month) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const addUtcMonthsClamped = (date, months) => {
  const desiredDay = date.getUTCDate();
  const targetMonthIndex = date.getUTCMonth() + months;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
  const targetDay = Math.min(desiredDay, daysInUtcMonth(targetYear, targetMonth));
  return new Date(Date.UTC(targetYear, targetMonth, targetDay));
};

const addUtcYearsClamped = (date, years) => {
  const targetYear = date.getUTCFullYear() + years;
  const targetMonth = date.getUTCMonth();
  const targetDay = Math.min(
    date.getUTCDate(),
    daysInUtcMonth(targetYear, targetMonth)
  );
  return new Date(Date.UTC(targetYear, targetMonth, targetDay));
};

const UNSUPPORTED_EXISTING_PROMISE_FREQUENCIES = new Set([
  "every2months",
  "bimonthly",
  "every4months",
  "semiannually",
]);

export const isUnsupportedExistingPromiseFrequency = (frequency) => {
  const normalized = String(frequency || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  return UNSUPPORTED_EXISTING_PROMISE_FREQUENCIES.has(normalized);
};

const advancePaymentDate = (date, frequency) => {
  const value = String(frequency || "monthly").toLowerCase();

  if (value === "daily") {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + 1);
    return next;
  }
  if (value === "weekly") {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + 7);
    return next;
  }
  if (value === "every_2_weeks" || value === "biweekly") {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + 14);
    return next;
  }
  if (value === "every_2_months" || value === "bimonthly") {
    return addUtcMonthsClamped(date, 2);
  }
  if (value === "quarterly") return addUtcMonthsClamped(date, 3);
  if (value === "every_4_months") return addUtcMonthsClamped(date, 4);
  if (value === "semi_annually") return addUtcMonthsClamped(date, 6);
  if (value === "annually" || value === "yearly") {
    return addUtcYearsClamped(date, 1);
  }
  return addUtcMonthsClamped(date, 1);
};

const countPayments = (startDate, endDate, frequency) => {
  // Current split promises always have an end date; keep two payments as a
  // safe fallback for legacy records created before that schedule was stored.
  if (!endDate) return 2;
  const start = parseDateOnlyUtc(startDate);
  const end = parseDateOnlyUtc(endDate);
  if (!start || !end || end <= start) return 2;

  let count = 1;
  let current = new Date(start);

  while (current < end && count < 1000) {
    current = advancePaymentDate(current, frequency);
    if (current > end) break;
    count += 1;
  }

  return Math.max(2, count);
};

const futureEndDate = (value, startDate) => {
  const end = parseDateOnlyUtc(value);
  const start = parseDateOnlyUtc(startDate);
  if (!end || !start || end <= start) return null;
  return formatDateOnlyUtc(end);
};

const addPaymentPeriods = (startDate, frequency, periods) => {
  let date = parseDateOnlyUtc(startDate);
  if (!date) return null;

  for (let index = 0; index < periods; index += 1) {
    date = advancePaymentDate(date, frequency);
  }

  return formatDateOnlyUtc(date);
};

const ExistingPromisePayment = ({ t, promise, onContinue }) => {
  const isSplit = promise?.promise_type === "split_payment";
  const [startDate, setStartDate] = useState(toDateInput(promise?.due_date));
  const remainingAmount = Number(promise?.remaining_amount || 0);
  const hasUnsupportedFrequency =
    isSplit && isUnsupportedExistingPromiseFrequency(promise?.payment_frequency);
  const numberOfPayments = useMemo(
    () => (isSplit ? countPayments(startDate, promise?.end_date, promise?.payment_frequency) : null),
    [isSplit, startDate, promise?.end_date, promise?.payment_frequency]
  );
  const perPaymentAmount = isSplit
    ? Math.floor(
        Math.round((remainingAmount + Number.EPSILON) * 100) / numberOfPayments
      ) / 100
    : null;
  const resolvedEndDate = isSplit
    ? futureEndDate(promise?.end_date, startDate) ||
      addPaymentPeriods(
        startDate,
        promise?.payment_frequency,
        Math.max(1, numberOfPayments - 1)
      )
    : null;

  const handleContinue = () => {
    if (hasUnsupportedFrequency) return;

    onContinue({
      amount: remainingAmount,
      paymentType: isSplit ? "split" : "one_time",
      frequency: isSplit ? promise.payment_frequency || "monthly" : null,
      startDate: isSplit ? startDate : null,
      endDate: resolvedEndDate,
      numberOfPayments,
      perPaymentAmount,
      surchargeEnabled: false,
    });
  };

  return (
    <Box sx={{ maxWidth: 580, mx: "auto" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <VolunteerActivismIcon sx={{ fontSize: 54, color: "primary.main", mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
          {t("review_promise", "Review Your Promise")}
        </Typography>
        <Typography color="text.secondary">
          {t("review_promise_description", "Confirm the remaining balance before continuing to payment.")}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t("campaign", "Campaign")}</Typography>
            <Typography sx={{ fontWeight: 600 }}>{promise?.campaign_name || "-"}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t("promise_date", "Promise date")}</Typography>
            <Typography>
              {promise?.created_at ? new Date(promise.created_at).toLocaleDateString() : "-"}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t("promised_amount", "Promised")}</Typography>
            <Typography sx={{ fontWeight: 600 }}>${Number(promise?.promise_amount || 0).toFixed(2)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t("given_amount", "Given")}</Typography>
            <Typography>${Number(promise?.given_amount || 0).toFixed(2)}</Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontWeight: 700 }}>{t("amount_left", "Amount left")}</Typography>
            <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: "1.2rem" }}>
              ${remainingAmount.toFixed(2)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t("payment_type", "Payment type")}</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {isSplit ? t("split_payment", "Split payment") : t("one_time", "One-time payment")}
            </Typography>
          </Stack>
          {isSplit && (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t("frequency", "Frequency")}</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {t(promise?.payment_frequency, promise?.payment_frequency || "monthly")}
                </Typography>
              </Stack>
              {!hasUnsupportedFrequency && (
                <TextField
                  type="date"
                  label={t("first_payment_date", "First payment date")}
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              )}
              {hasUnsupportedFrequency ? (
                <Alert severity="warning">
                  {t(
                    "unsupported_existing_promise_frequency",
                    "This promise uses a payment frequency that is no longer supported for scheduled payments. Please contact the organization to arrange payment."
                  )}
                </Alert>
              ) : (
                <Alert severity="info">
                  {t("payment_schedule_summary", {
                    defaultValue: `${numberOfPayments} payments of approximately $${perPaymentAmount.toFixed(2)}.`,
                    count: numberOfPayments,
                    amount: perPaymentAmount.toFixed(2),
                  })}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </Paper>

      <FriendlyButton
        onClick={handleContinue}
        endIcon={<ArrowForwardIcon />}
        fullWidth
        disabled={!remainingAmount || (isSplit && (!startDate || hasUnsupportedFrequency))}
      >
        {t("continue_to_payment", "Continue to Payment")}
      </FriendlyButton>
    </Box>
  );
};

export default ExistingPromisePayment;
